import { useRouter } from 'next/router';
import { logEventAsArray } from '@/containers/AnalyticsContainer';
import { useFormContext } from 'react-hook-form';
import {
  addressSteps,
  contractSteps,
  editSejamSteps,
  initialRegisterSteps,
  profilingSteps,
  registerRedirectSteps,
  registerSteps,
} from '../constants';
import Toast from '@/components/Toast';
import { useGetUserStatus } from '@/userStatus/hooks';
import { removeRequestToken, setRequestToken } from '../utils';
import { TRedirectData } from '../types';
import { useLogin } from './useLogin';
import { useRegisterLimitations } from './useRegisterLimitations';
import { useRegisterMessages } from './useRegisterMessages';

const {
  Exam,
  InquiryIdentityStatus,
  BirthDate,
  Signature,
  Video,
  TraceCode,
  WaitingEkyc,
  NationalCardSerial,
  Finish,
  RedirectToOldReg,
  GetTraceCode,
  TraceCodeVerifyOtp,
  MofidOtp,
  Password,
  MofidLogin,
} = registerSteps;

export const useRegister = () => {
  const router = useRouter();
  const setValue = useFormContext()?.setValue;
  const { isAnonymous } = useGetUserStatus();
  const { registerLink } = useRegisterLimitations();
  const { handleRegisterMessages, isMessageDefined } = useRegisterMessages();
  const { handleUtmLogin } = useLogin();

  const handleInitialLoginLogic = (regState: any) => {
    // This is for when user's nationalIdVerified is false and user
    // has remaining reg steps.
    if (initialRegisterSteps.includes(regState?.nextStep)) {
      router.push('/register');
      removeRequestToken();
    } else {
      const redirectData = {
        nextStep: regState?.nextStep,
        requestToken: regState?.requestToken,
        message: regState?.nextStepMessage,
      };
      handleRegisterRedirect(redirectData);
    }
  };

  const needRegisterRedirect = (redirectData: TRedirectData) => {
    const { nextStep } = redirectData;
    return !!registerRedirectSteps.includes(nextStep);
  };

  const handleRegisterRedirect = (redirectData: TRedirectData) => {
    const { nextStep, requestToken } = redirectData;
    if (requestToken) setRequestToken(requestToken);

    if (nextStep === RedirectToOldReg) {
      router.push(redirectData?.message?.link || '');
    }
    if (nextStep === Finish) {
      removeRequestToken();
      router.push('/');
    }

    if (isAnonymous) {
      // If there's a redirect from register flow, it should redirect to account login
      handleUtmLogin();
    } else {
      if (contractSteps?.includes(nextStep)) {
        router.push(`/register/contracts`);
      } else if (nextStep === Exam) {
        router.push(`/register/exam`);
      } else if (nextStep === InquiryIdentityStatus) {
        router.push(`/register/register-status`);
      } else if (nextStep === BirthDate) {
        router.push(`/register/credentials`);
      } else if (nextStep === Signature) {
        router.push(`/register/upload-signature`);
      } else if (nextStep === Video) {
        router.push(`/register/videoAuthentication`);
      } else if (addressSteps.includes(nextStep)) {
        router.push(`/register/address?state=${nextStep}`);
      } else if (nextStep === TraceCode) {
        router.push(`/register/traceCode`);
      } else if (nextStep === WaitingEkyc) {
        router.push(`/register/ekyc-approval`);
      } else if (nextStep === NationalCardSerial) {
        router.push(`/register/national-serial`);
      } else if (profilingSteps.includes(nextStep)) {
        router.push(`/register/verify-profile`);
      } else if (initialRegisterSteps.includes(nextStep)) {
        router.push(`/register`);
      } else if (nextStep === MofidLogin) {
        router.push(`/register/completed?shouldLogin=true`);
      } else {
        // If step is not declared, remove request token and push to main page
        removeRequestToken();
        router.push('/');
      }
    }
  };

  const handleRegisterLogic = (redirectData: TRedirectData) => {
    const { nextStep, requestToken, phoneNumber } = redirectData;

    if (requestToken) setRequestToken(requestToken);

    const shouldShowMessage = isMessageDefined(redirectData);

    if (needRegisterRedirect(redirectData) && !shouldShowMessage) {
      handleRegisterRedirect(redirectData);
      return;
    }

    if (redirectData && setValue) setValue('redirectData', redirectData);
    if (phoneNumber && setValue) setValue('phoneNumber', phoneNumber);

    if (shouldShowMessage) {
      handleRegisterMessages({
        handleRegisterLogic,
        redirectData,
      });
    } else {
      // TODO: this whole else sections needs to get spread out into their own components
      if (editSejamSteps.includes(nextStep)) {
        setValue('stepNumber', '5');
        setValue('registerStatus', nextStep);
      } else if (nextStep === MofidOtp) {
        setValue('stepNumber', '2');
      } else if (nextStep === GetTraceCode) {
        setValue('stepNumber', '7');
      } else if (nextStep === TraceCodeVerifyOtp) {
        setValue('stepNumber', '8');
      } else if (nextStep === Password) {
        setValue('stepNumber', '4');
      }
    }
  };
  const handleRegisterError = (error: any) => {
    logEventAsArray([
      'REGISTER_ERROR',
      {
        status: error?.status,
        code: error?.code,
        serviceName: error?.serviceName,
        message: error?.message,
      },
    ]);
    if (
      ['450', 'TokenExpiredError', '401', 'Unauthorized'].includes(error?.code)
    ) {
      if (isAnonymous) {
        router.push(registerLink);
      } else {
        router.push('/');
      }
    }
    if (error.displayMode === 'Toast') {
      Toast.open({
        variant: 'error',
        children: error?.message,
      });
    }
  };

  return {
    needRegisterRedirect,
    handleRegisterRedirect,
    handleRegisterLogic,
    handleInitialLoginLogic,
    handleRegisterError,
  };
};
